import React, {useRef} from 'react';
import {
  FlatList,
  Image,
  Platform,
  RefreshControl,
  StyleSheet,
  TouchableHighlight,
  View,
} from 'react-native';
import {useHeaderHeight} from '@react-navigation/elements';
import {Observer, observer} from 'mobx-react-lite';

import BrandCarousel from '../../components/BrandCarousel';
import InStoreShoppingModal from '../../components/modals/InStoreShoppingModal';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import WebFooter from '../../components/WebFooter';
import {useSearchDispatch} from '../../context/SearchContext';
import {useScrollCollapseJSHandler} from '../../hooks/useScrollCollapse';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {Department} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {navigateToGiftCardsPromotionOrLoadingScreen, usePromotions} from '../../queries/promotions';
import {GIFT_CARDS_TILE, SPECIALS_TILE} from '../../stores/rangeStore';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {codeifyName} from '../../utils/string';

const ShopScreen = observer(({navigation}: RouteNavigatorScreenProps<AppRoute.SHOP>) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const listRef = useRef(null);
  const {scrollHandler} = useScrollCollapseJSHandler(listRef);
  const headerHeight = useHeaderHeight();
  const {rangeStore, filterStore} = useStores();
  const {rawPromotions} = usePromotions();
  const searchDispatch = useSearchDispatch();

  const tilesFlatListNumColumns = isWebDesktopScreen ? 4 : 2;

  const handleDepartmentTilePress = (department: Department) => {
    if (department.id === GIFT_CARDS_TILE) {
      navigateToGiftCardsPromotionOrLoadingScreen(rawPromotions, navigation.navigate, false);
      return;
    }

    rangeStore.selectDepartment(department);
    filterStore.clearSortAndFilters();
    const subDepartments = rangeStore.getSubDepartments();
    if (!subDepartments.length) {
      rangeStore.selectSubDepartment(department);
    }
    if (department.id === SPECIALS_TILE) {
      searchDispatch({type: 'SCOPE_TO_DEPARTMENT', departmentId: '', specials: true});
    } else {
      searchDispatch({type: 'SCOPE_TO_DEPARTMENT', departmentId: department.id});
    }

    navigation.navigate(AppRoute.DEPARTMENT, {
      department: department,
    });
  };

  const tileWidthStyle = StyleSheet.create({
    main: {
      flex: 1 / tilesFlatListNumColumns,
    },
  });

  const departments = rangeStore.sortedAndFilteredDepartments;

  const renderDepartmentTile = ({item, index}: {item: Department; index: number}) => {
    const tileStyles = [styles.tile, tileWidthStyle.main];

    if ((index + 1) % tilesFlatListNumColumns !== 0) {
      tileStyles.push(styles.nonLastInRowTile);
    }

    return (
      <TouchableHighlight
        key={item.id}
        onPress={() => handleDepartmentTilePress(item)}
        activeOpacity={0.7}
        delayPressIn={150}
        style={tileStyles}
        testID={`department-tile-${codeifyName(item.name)}`}>
        <View style={styles.innerView}>
          <Image style={styles.tileImage} source={{uri: item.links.icon}} resizeMode="contain" />
          <View
            style={styles.tileTextHolder}
            testID={`department-tile-${codeifyName(item.name)}-text`}>
            <Text style={styles.tileText}>{item.name}</Text>
          </View>
        </View>
      </TouchableHighlight>
    );
  };

  return (
    <ContentView testID="shop-screen">
      <Observer>
        {() => (
          <FlatList
            ref={listRef}
            testID="range-department-screen-flatlist"
            key={`range-flatlist-${tilesFlatListNumColumns}`}
            data={departments}
            keyExtractor={item => String(item.id)}
            renderItem={renderDepartmentTile}
            horizontal={false}
            numColumns={tilesFlatListNumColumns}
            columnWrapperStyle={styles.columnStyle}
            style={isWebDesktopScreen ? styles.webDesktoplist : styles.list}
            contentContainerStyle={{paddingTop: headerHeight}}
            onScroll={scrollHandler}
            scrollEventThrottle={16}
            ItemSeparatorComponent={() => (
              <View style={styles.separator} testID="range-department-screen-flatlist-separator" />
            )}
            // TODO: The WebFooter should be in a global/common place
            ListFooterComponent={() => !!isWebDesktopScreen && <WebFooter />}
            ListHeaderComponent={() => <BrandCarousel />}
            ListHeaderComponentStyle={styles.header}
            showsVerticalScrollIndicator={isWebDesktopScreen}
            {...(Platform.OS === 'android' && {overScrollMode: 'never' as const})}
            refreshControl={
              <RefreshControl refreshing={false} onRefresh={() => rangeStore.loadDepartments()} />
            }
          />
        )}
      </Observer>
      <InStoreShoppingModal />
    </ContentView>
  );
});

export default ShopScreen;

const styles = StyleSheet.create({
  main: {
    flex: 1,
  },
  webDesktoplist: {
    overflow: 'visible',
  },
  list: {
    backgroundColor: theme.colour.background.range.tileContainer,
    overflow: 'visible',
  },
  separator: {
    height: 0,
    borderTopWidth: 1,
    borderColor: 'grey',
  },
  columnStyle: {},
  tile: {
    paddingVertical: 4,
    backgroundColor: theme.colour.background.range.tileContainer,
  },
  nonLastInRowTile: {
    borderRightWidth: 0.5,
    borderRightColor: 'grey',
  },
  innerView: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileImage: {
    width: '40%',
    aspectRatio: 1,
    marginTop: 8,
  },
  tileTextHolder: {
    paddingHorizontal: 4,
    marginVertical: 8,
  },
  tileText: {
    textAlign: 'center',
    fontSize: 16,
    color: 'white',
  },
  header: {
    width: '100%',
    overflow: 'visible',
  },
});
